import React, { useRef } from 'react';
import Portal from '../Portal/Portal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ElementSize } from '../../types/element-size';
import { ButtonType } from '../../types/button-type';
import IconCrossRound from '../Icons/IconCrossRound/IconCrossRound';

type ComponentProps = {
  acceptBtnText?: string;
  cancelBtnText?: string;
  children?: React.ReactNode;
  className?: string;
  isVisible: boolean;
  modalTitle: string;
  modalSubTitle?: string;
  onClose: (data: any) => void;
  onAccept?: (data: any) => void;
  showAcceptBtn?: boolean;
  showCancelBtn?: boolean;
};

const ModalFullScreen: React.FC<ComponentProps> = ({
  acceptBtnText = 'Create',
  cancelBtnText = 'Cancel',
  className = '',
  showAcceptBtn = false,
  showCancelBtn = false,
  modalTitle,
  modalSubTitle,
  children,
  isVisible,
  onClose,
  onAccept,
}) => {
  const modalOverlayClassName = 'c-modal__overlay fixed left-0 top-0 z-9999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 p-3';
  const modalClassName = `c-modal-full-screen-full-screen flex flex-col relative w-full h-full items-center sm:rounded-lg bg-white ${className}`;

  // Close only when both the mousedown AND the mouseup happened directly on
  // the overlay itself - see the equivalent comment in Modal.tsx for why a
  // plain onClick={onClose} on the overlay closes it when a click/drag
  // starts inside the modal (e.g. selecting text in an input) and is
  // released outside its bounds.
  const isOverlayMouseDown = useRef(false);

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isOverlayMouseDown.current = e.target === e.currentTarget;
  };

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const shouldClose = isOverlayMouseDown.current && e.target === e.currentTarget;
    isOverlayMouseDown.current = false;

    if (shouldClose) {
      onClose(e);
    }
  };

  return (
    <Portal>
      {isVisible && (
        <div
          className={modalOverlayClassName}
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayMouseUp}
        >
          <div
            className={modalClassName}
          >
            <div className="c-modal__header min-w-full flex items-center justify-between gap-x-4 border-b border-foggy-mist px-4 py-2 mb-4">
              <div className={`flex flex-col ${modalSubTitle ? 'gap-y-2' : ''}`}>
                <h3 className="text-xl font-bold text-black dark:text-white sm:text-2xl">
                  {modalTitle}
                </h3>
                {modalSubTitle && <span className='text-sm text-slate-gray dark:text-white'>{modalSubTitle}</span>}
              </div>
              <IconCrossRound
                fill='#FF0000'
                onClick={onClose}
                size={modalSubTitle ? ElementSize.XL : ElementSize.LG}
              />
            </div>
            <div className='c-modal__content overflow-auto w-full h-full px-4'>
              {children}
              {(showAcceptBtn || showCancelBtn) &&
                <div className="c-modal__footer w-full flex flex-row justify-between pt-4">
                  {showAcceptBtn && (
                    <InteractiveButton
                      onClick={onClose}
                      text={cancelBtnText}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  )}
                  {showCancelBtn && (
                    <InteractiveButton
                      onClick={onAccept}
                      text={acceptBtnText}
                      size={ElementSize.XL}
                    />
                  )}
                </div>
              }
            </div>
          </div>
        </div>
      )}
    </Portal>
  );
};

export default ModalFullScreen;
